import axios from 'axios'
import auth from '@/core/auth'
import alert from '@/core/alert'
import httpHelper from '@/helpers/httpHelper'
import Vue from 'vue'

const localHosts = [
  'localhost',
  '127.0.0.1',
  '192.168.0.31',
  '192.168.1.2',
  '192.168.1.4',
  '192.168.178.21',
  '192.168.178.85',
  'test.bauexperts.de',
  'w1.bxp.local',
  'w2.bxp.local'
]

export default {

  requestCount: 0,

  currentRequests: {},

  isLocal () {
    return localHosts.includes(window.location.hostname)
  },

  new () {
    let domain = ''
    if (!this.isLocal()) {
      domain = 'https://service.bauexperts.de'
    }

    var instance = axios.create({
      baseURL: `${domain}/api/`,
      headers: {
        Authorization: 'Bearer ' + auth.getToken(),
        'Content-type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Workspace': httpHelper.getSubdomain(location.hostname)
      }
    })

    instance.interceptors.response.use(function (response) {
      return response
    }, function (error) {
      return Promise.reject(error)
    })

    return instance
  },

  onError (error) {
    if (!error.response) {
      console.error(error)
      alert.error('Es ist leider ein Fehler aufgetreten. Bitte versuchen Sie die Seite neu zu laden oder ' +
      'versuchen Sie es später erneut.')
      if (window.Sentry) {
        window.Sentry.withScope(scope => {
          if (auth && auth.user) {
            scope.setUser(auth.user)
          }
          window.Sentry.captureException(error)
        })
      }
    } else if (error.response.status === 400) {
      // This means that this is an form validation error
      alert.error('Validierung fehlgeschlagen. Einträge entweder fehlend oder fehlerhaft.')
    } else if (error.response.status === 401) {
      // This means the token is not acceptable
      auth.logout()
      window.router.push({ name: 'login' })
    } else if (error.response.status === 403) {
      alert.error('Sie verfügen nicht über die benötigten Zugriffsrechte.')
    } else if (error.response.status === 404) {
      alert.error('Die angeforderte Ressource wurde nicht gefunden.')
    } else if (error.response.status === 406) {
      // This means that api throw an exception
      alert.error(error.response.data.message)
    }
  },

  get (url, data, callback, onError, cancel = false) {
    const instance = this.new()
    const dataObj = {
      params: data
    }

    if (cancel) {
      if (this.currentRequests[url]) {
        this.currentRequests[url].cancel()
      }
      this.currentRequests[url] = axios.CancelToken.source()

      instance.CancelToken = axios.CancelToken
      instance.isCancel = axios.isCancel

      dataObj.cancelToken = this.currentRequests[url].token
    }

    var self = this
    this.startProgress()

    instance.get(url, dataObj).then(function (response) {
      if (response.headers['bxp-appversion'] && auth.user) {
        auth.user.appversion = response.headers['bxp-appversion']
      }
      // TODO: arife somehow had an empty response?
      // https://sentry.io/organizations/bauexperts/issues/1837017446/?project=1435895&query=is%3Aunresolved
      callback(response.data)
      self.stopProgress()
    }).catch(function (error) {
      if (typeof onError === 'function') {
        try {
          onError(error)
        } catch (err) {
          console.error(err)
        }
      } else {
        if (instance.isCancel && instance.isCancel(error)) {
        } else {
          self.onError(error)
        }
      }
      self.stopProgress()
    })
  },

  post (url, data, callback, onError) {
    var self = this
    this.startProgress()
    this.new().post(url, data).then(function (response) {
      callback(response.data)
      self.stopProgress()
    }).catch(function (error) {
      if (typeof onError === 'function') {
        try {
          onError(error)
        } catch (err) {
          console.error(err)
        }
      } else {
        self.onError(error)
      }
      self.stopProgress()
    })
  },

  postProgress (url, data, callback, onError, progressChanged) {
    var self = this
    this.startProgress()
    this.new().post(url, data, {
      onUploadProgress: evt => (progressChanged(Math.floor(evt.loaded * 100 / evt.total))),
      onDownloadProgress: evt => (progressChanged(Math.floor(evt.loaded * 100 / evt.total)))
    }).then(function (response) {
      callback(response.data)
      self.stopProgress()
    }).catch(function (error) {
      if (typeof onError === 'function') {
        try {
          onError(error)
        } catch (err) {
          console.error(err)
        }
      } else {
        self.onError(error)
      }
      self.stopProgress()
    })
  },

  put (url, data, callback, onError) {
    var self = this
    this.startProgress()
    this.new().put(url, data).then(function (response) {
      callback(response.data)
      self.stopProgress()
    }).catch(function (error) {
      if (typeof onError === 'function') {
        try {
          onError(error)
        } catch (err) {
          console.error(err)
        }
      } else {
        self.onError(error)
      }
      self.stopProgress()
    })
  },

  delete (url, data, callback, onError) {
    var self = this
    this.startProgress()
    this.new().delete(url, data).then(function (response) {
      callback(response.data)
      self.stopProgress()
    }).catch(function (error) {
      if (typeof onError === 'function') {
        try {
          onError(error)
        } catch (err) {
          console.error(err)
        }
      } else {
        self.onError(error)
      }
      self.stopProgress()
    })
  },

  getSync (url, data, onError) {
    return new Promise(resolve => this.get(url, data, resolve, onError))
  },

  postSync (url, data, onError) {
    return new Promise(resolve => this.post(url, data, resolve, onError))
  },

  putSync (url, data, onError) {
    return new Promise(resolve => this.put(url, data, resolve, onError))
  },

  deleteSync (url, data, onError) {
    return new Promise(resolve => this.delete(url, data, resolve, onError))
  },

  startProgress () {
    this.requestCount++
    if (Vue.prototype.$sharedData) {
      Vue.prototype.$sharedData.isSubmitting = true
    }

    if (window.nprogress) {
      window.nprogress.start()
    }
  },

  stopProgress () {
    if (this.requestCount > 0) {
      this.requestCount--
    }

    if (this.requestCount === 0) {
      if (Vue.prototype.$sharedData) {
        Vue.prototype.$sharedData.isSubmitting = false
      }

      if (window.nprogress) {
        window.nprogress.done()
      }
    }
  }
}
